import React, { useState, useCallback, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Chip,
  Paper,
  Grid,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputAdornment,
  IconButton,
  Tooltip,
  Avatar,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import CloudUploadIcon from "@material-ui/icons/CloudUpload";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import AddIcon from "@material-ui/icons/Add";
import SearchIcon from "@material-ui/icons/Search";
import ClearIcon from "@material-ui/icons/Clear";
import DownloadIcon from "@material-ui/icons/GetApp";
import PeopleAltIcon from "@material-ui/icons/PeopleAlt";
import DescriptionIcon from "@material-ui/icons/Description";
import PaymentIcon from "@material-ui/icons/Payment";
import EditIcon from "@material-ui/icons/Edit";
import { setLoading, setSnackInfo } from "../../redux/actions/appAction";
import { connect, useSelector } from "react-redux";
import HttpService from "../../services/httpService";
import { saveCsvBlob } from "../../utils/panelDownload";
import { ONBOARDING_APPROVAL_TYPES } from "../../config/onboardingApprovalTypesRegistry";
import { ROLENAME, constantDepartmentId } from "../../utils/index";

const CHANNEL_PARTNER_TYPE = ONBOARDING_APPROVAL_TYPES.find((t) => t.key === "channelPartner");
const DEFAULT_ROWS_PER_PAGE = 200;
const ROWS_PER_PAGE_OPTIONS = [50, 100, 200, 500];

const useStyles = makeStyles((theme) => ({
  root: { padding: theme.spacing(3), width: "100%" },
  header: {
    marginBottom: theme.spacing(3),
    padding: theme.spacing(3),
    background: "linear-gradient(135deg, #1A3C6E 0%, #2E75B6 100%)",
    borderRadius: theme.spacing(2),
    color: "#fff",
    boxShadow: "0 8px 32px rgba(26, 60, 110, 0.3)",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    flexWrap: "wrap",
    gap: theme.spacing(2),
  },
  headerTitle: {
    fontWeight: 600,
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  headerSubtitle: {
    opacity: 0.9,
    marginTop: theme.spacing(0.5),
  },
  createButton: {
    backgroundColor: "#fff",
    color: "#1A3C6E",
    fontWeight: 600,
    textTransform: "none",
    borderRadius: theme.spacing(1),
    padding: theme.spacing(1, 3),
    "&:hover": { backgroundColor: "#eef2fb" },
  },
  raiseButton: {
    borderColor: "#fff",
    color: "#fff",
    fontWeight: 600,
    textTransform: "none",
    borderRadius: theme.spacing(1),
    padding: theme.spacing(1, 3),
    "&:hover": { backgroundColor: "rgba(255,255,255,0.12)", borderColor: "#fff" },
  },
  headerActions: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
    flexWrap: "wrap",
  },
  toolbar: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    flexWrap: "wrap",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  searchField: {
    backgroundColor: "#fff",
    borderRadius: theme.spacing(1),
    minWidth: 320,
  },
  downloadButton: {
    borderRadius: theme.spacing(1),
    textTransform: "none",
    fontWeight: 600,
  },
  tablePaper: {
    borderRadius: theme.spacing(2),
    overflow: "hidden",
    boxShadow: "0 2px 12px rgba(26, 60, 110, 0.1)",
  },
  tableHeadCell: {
    fontWeight: 700,
    color: "#5b6b82",
    backgroundColor: "#f4f6fb",
    borderBottom: "2px solid #e3e8f2",
  },
  tableRow: {
    cursor: "pointer",
    transition: "background-color 0.15s ease",
    "&:hover": { backgroundColor: "#f7f9fd" },
  },
  tableCell: { borderBottom: "1px solid #eef1f7" },
  nameCell: { display: "flex", alignItems: "center", gap: theme.spacing(1.5) },
  avatar: {
    width: 34,
    height: 34,
    fontSize: "0.85rem",
    fontWeight: 700,
    backgroundColor: "#2E75B6",
  },
  fullName: { fontWeight: 600, color: "#1A3C6E" },
  sectionTitle: { fontWeight: 700, marginBottom: theme.spacing(2), color: "#1A3C6E" },
  uploadBox: {
    border: "2px dashed #c5cae9",
    borderRadius: theme.spacing(1.5),
    padding: theme.spacing(2),
    textAlign: "center",
    cursor: "pointer",
    "&:hover": { borderColor: "#3949ab" },
  },
  fileName: { fontSize: "0.8rem", color: "#4caf50", marginTop: theme.spacing(1) },
  statusChip: { fontWeight: 600 },
  emptyState: { padding: theme.spacing(6), textAlign: "center" },
  paginationContainer: {
    marginTop: theme.spacing(2),
    backgroundColor: "#fff",
    borderRadius: theme.spacing(1),
    boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
  },
}));

const EMPTY_FORM = {
  full_name: "", email: "", mobile: "", pan_number: "", aadhaar: "",
  full_address: "", city: "", state: "", pincode: "",
  account_name: "", bank_name: "", account_number: "", confirm_account_number: "", branch_name: "", ifsc_code: "", account_type: "savings",
};

const FIELD_LABELS = {
  full_name: "Full Name", email: "Email id", mobile: "Mobile no.", pan_number: "PAN No.", aadhaar: "Aadhaar No.",
  full_address: "Full Address", city: "City", state: "State", pincode: "ZIP / PIN Code",
  account_name: "Account Name", bank_name: "Bank Name", account_number: "Bank A/C No.",
  confirm_account_number: "Confirm Bank A/C No.", branch_name: "Bank's Branch Name", ifsc_code: "IFSC Code",
  account_type: "Account Type",
  pan_doc: "PAN Card Image", aadhaar_doc: "Aadhaar Card Image", cheque_doc: "Cancelled Cheque Image",
};

const ACCOUNT_TYPES = [
  { value: "savings", label: "Savings" },
  { value: "current", label: "Current" },
  { value: "nro", label: "NRO" },
  { value: "nre", label: "NRE" },
];

// Letters, digits and spaces only — no special characters or commas.
const NO_SPECIAL_CHARS_REGEX = /^[A-Za-z0-9 ]+$/;

const STATUS_COLORS = {
  pending_verification: { background: "#fff8e1", color: "#f57f17" },
  active: { background: "#e8f5e9", color: "#2e7d32" },
  rejected: { background: "#ffebee", color: "#c62828" },
};
const STATUS_LABELS = { pending_verification: "Pending Finance Approval", active: "Approved", rejected: "Rejected" };

const csvEscape = (value) => {
  const str = value === null || value === undefined ? "" : String(value);
  return /[",\n]/.test(str) ? `"${str.replace(/"/g, '""')}"` : str;
};

const ChannelPartnerOnboarding = (props) => {
  const classes = useStyles();
  const [formMode, setFormMode] = useState(null); // null | "create" | "edit"
  const [editingRecord, setEditingRecord] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const [files, setFiles] = useState({});
  const [uploading, setUploading] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [channelPartners, setChannelPartners] = useState([]);
  const [loadingList, setLoadingList] = useState(false);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0); // 0-indexed for TablePagination
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);
  const [searchInput, setSearchInput] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);

  const [payoutDialogOpen, setPayoutDialogOpen] = useState(false);
  const [activeChannelPartners, setActiveChannelPartners] = useState([]);
  const [payoutChannelPartnerId, setPayoutChannelPartnerId] = useState("");
  const [payoutLeadNo, setPayoutLeadNo] = useState("");
  const [payoutSubmitting, setPayoutSubmitting] = useState(false);
  const [lastRaisedPayout, setLastRaisedPayout] = useState(null);

  const { userDetails } = useSelector((state) => state.user);
  const isBelongToFinance = userDetails?.departmentId === constantDepartmentId.FinanceDepartment;
  const isAdmin = !!userDetails?.roles?.find((role) => role?.name === ROLENAME.Admin);
  const canEdit = (record) => (
    !!record && (String(record.onboarded_by?._id || record.onboarded_by) === String(userDetails?._id) || isBelongToFinance || isAdmin)
  );

  const fetchChannelPartners = useCallback(async () => {
    setLoadingList(true);
    try {
      const res = await HttpService.getAllChannelPartners({
        page: page + 1,
        limit: rowsPerPage,
        name: appliedSearch.trim() || undefined,
      });
      setChannelPartners(res?.data?.data || []);
      setTotal(res?.data?.total ?? (res?.data?.data || []).length);
    } catch (err) {
      props.setSnackInfo("Failed to load Channel Partners", "error");
    }
    setLoadingList(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, appliedSearch]);

  useEffect(() => { fetchChannelPartners(); }, [fetchChannelPartners]);

  const submitSearch = () => {
    setAppliedSearch(searchInput);
    setPage(0);
  };

  const clearSearch = () => {
    setSearchInput("");
    setAppliedSearch("");
    setPage(0);
  };

  const handleDownloadCsv = () => {
    const header = [
      "Sl. No.", "CP Code", "Name", "Mobile", "Email", "PAN", "Aadhaar (Masked)",
      "Account Name", "Bank Name", "Account Number", "Account Type", "Branch Name", "IFSC Code",
      "Onboarded By", "Updated", "Status",
    ];
    const rows = channelPartners.map((record, index) => [
      page * rowsPerPage + index + 1,
      record.cp_code || "",
      record.full_name || "",
      record.mobile || "",
      record.email || "",
      record.pan_number || "",
      record.aadhaar_masked || "",
      record.bank?.account_name || "",
      record.bank?.bank_name || "",
      record.bank?.account_number || "",
      record.bank?.account_type || "",
      record.bank?.branch_name || "",
      record.bank?.ifsc_code || "",
      record.onboarded_by?.name || "",
      record.updated_at ? new Date(record.updated_at).toLocaleString() : "",
      STATUS_LABELS[record.status] || record.status || "",
    ]);
    const csv = [header, ...rows].map((row) => row.map(csvEscape).join(",")).join("\n");
    saveCsvBlob(csv, `channel-partners-${new Date().toISOString().slice(0, 10)}.csv`);
  };

  const openDetails = (record) => {
    setSelectedRecord(record);
    setDetailsOpen(true);
  };
  const closeDetails = () => setDetailsOpen(false);

  const setField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => ({ ...prev, [key]: undefined }));
  };

  const handleFileUpload = useCallback(async (fieldKey, fileObj) => {
    if (!fileObj) return;
    if (fileObj.size > 10 * 1024 * 1024) {
      props.setSnackInfo(`File "${fileObj.name}" exceeds 10MB limit`, "error");
      return;
    }
    const allowedTypes = ["application/pdf", "image/jpeg", "image/jpg", "image/png"];
    if (!allowedTypes.includes(fileObj.type)) {
      props.setSnackInfo(`File type "${fileObj.type}" is not supported. Please upload PDF, JPG or PNG.`, "error");
      return;
    }
    setUploading((prev) => ({ ...prev, [fieldKey]: true }));
    try {
      const formData = new FormData();
      formData.append("file", fileObj);
      const response = await HttpService.uploadFile(formData);
      const s3Link = response.data?.s3Link || response.data?.url || response.data;
      setFiles((prev) => ({
        ...prev,
        [fieldKey]: { name: fileObj.name, size: fileObj.size, type: fileObj.type, s3Link, uploadedAt: new Date().toISOString() },
      }));
      setErrors((prev) => ({ ...prev, [fieldKey]: undefined }));
      props.setSnackInfo(`"${fileObj.name}" uploaded`, "success");
    } catch (err) {
      props.setSnackInfo(`Failed to upload "${fileObj.name}": ${err?.response?.data?.message || err?.message || "Unknown error"}`, "error");
    }
    setUploading((prev) => ({ ...prev, [fieldKey]: false }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const validate = () => {
    const next = {};
    const aadhaarOptional = formMode === "edit";
    Object.keys(EMPTY_FORM).forEach((key) => {
      if (key === "aadhaar" && aadhaarOptional) return; // edit mode: blank = keep existing
      if (!form[key] || !String(form[key]).trim()) next[key] = "Required";
    });
    if (form.mobile && !/^[6-9]\d{9}$/.test(form.mobile.trim())) next.mobile = "Invalid 10-digit mobile number";
    if (form.pan_number && !/^[A-Z]{5}[0-9]{4}[A-Z]$/.test(form.pan_number.trim().toUpperCase())) next.pan_number = "Invalid PAN format (e.g. ABCDE1234F)";
    if (form.aadhaar && !/^\d{12}$/.test(form.aadhaar.replace(/\s/g, ""))) next.aadhaar = "Aadhaar must be 12 digits";
    if (form.pincode && !/^\d{6}$/.test(form.pincode.trim())) next.pincode = "Invalid 6-digit PIN code";
    if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) next.email = "Invalid email address";
    if (form.ifsc_code && !/^[A-Z]{4}0[A-Z0-9]{6}$/.test(form.ifsc_code.trim().toUpperCase())) next.ifsc_code = "Invalid IFSC code";
    if (form.account_number && form.account_number.trim().length > 18) next.account_number = "Must not exceed 18 characters";
    if (form.account_number && form.confirm_account_number && form.account_number.trim() !== form.confirm_account_number.trim()) {
      next.confirm_account_number = "Does not match Bank A/C No.";
    }
    ["account_name", "bank_name", "branch_name"].forEach((key) => {
      if (form[key] && !NO_SPECIAL_CHARS_REGEX.test(form[key].trim())) {
        next[key] = "No special characters or commas allowed";
      }
    });
    ["pan_doc", "aadhaar_doc", "cheque_doc"].forEach((key) => {
      if (!files[key]?.s3Link) next[key] = "Required";
    });
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const resetForm = () => {
    setForm(EMPTY_FORM);
    setFiles({});
    setErrors({});
  };

  const openCreateForm = () => {
    resetForm();
    setEditingRecord(null);
    setFormMode("create");
  };

  const openEditForm = (record) => {
    setForm({
      full_name: record.full_name || "",
      email: record.email || "",
      mobile: record.mobile || "",
      pan_number: record.pan_number || "",
      aadhaar: "", // never returned by the API — left blank, the update keeps the existing number
      full_address: record.address?.full_address || "",
      city: record.address?.city || "",
      state: record.address?.state || "",
      pincode: record.address?.pincode || "",
      account_name: record.bank?.account_name || "",
      bank_name: record.bank?.bank_name || "",
      account_number: record.bank?.account_number || "",
      confirm_account_number: record.bank?.account_number || "",
      branch_name: record.bank?.branch_name || "",
      ifsc_code: record.bank?.ifsc_code || "",
      account_type: record.bank?.account_type || "savings",
    });
    setFiles({
      pan_doc: record.pan_doc ? { name: "Existing PAN Card", s3Link: record.pan_doc, existing: true } : {},
      aadhaar_doc: record.aadhaar_doc ? { name: "Existing Aadhaar Card", s3Link: record.aadhaar_doc, existing: true } : {},
      cheque_doc: record.cheque_doc ? { name: "Existing Cancelled Cheque", s3Link: record.cheque_doc, existing: true } : {},
    });
    setErrors({});
    setEditingRecord(record);
    setFormMode("edit");
    setDetailsOpen(false);
  };

  const closeForm = () => {
    setFormMode(null);
    setEditingRecord(null);
    resetForm();
  };

  const openPayoutDialog = async () => {
    setPayoutChannelPartnerId("");
    setPayoutLeadNo("");
    setLastRaisedPayout(null);
    setPayoutDialogOpen(true);
    try {
      const res = await HttpService.getAllChannelPartners({ status: "active" });
      setActiveChannelPartners(res?.data?.data || []);
    } catch (err) {
      props.setSnackInfo("Failed to load approved Channel Partners", "error");
    }
  };

  const closePayoutDialog = () => setPayoutDialogOpen(false);

  const canSubmitPayout = payoutChannelPartnerId && payoutLeadNo.trim() && !payoutSubmitting;

  const handleRaisePayout = async () => {
    setPayoutSubmitting(true);
    try {
      const res = await HttpService.raiseChannelPartnerPayout({ channelPartnerId: payoutChannelPartnerId, leadNo: payoutLeadNo.trim() });
      if (res?.data?.success) {
        props.setSnackInfo(res.data.message || "Payout raised — pending Finance approval", "success");
        setLastRaisedPayout(res.data.data);
        setPayoutChannelPartnerId("");
        setPayoutLeadNo("");
      } else {
        props.setSnackInfo(res?.data?.message || "Failed to raise payout", "error");
      }
    } catch (err) {
      props.setSnackInfo(err?.response?.data?.message || "Failed to raise payout", "error");
    }
    setPayoutSubmitting(false);
  };

  const handleSubmit = async () => {
    if (!validate()) {
      props.setSnackInfo("Please fill every mandatory field correctly before submitting", "error");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        full_name: form.full_name.trim(),
        email: form.email.trim(),
        mobile: form.mobile.trim(),
        pan_number: form.pan_number.trim().toUpperCase(),
        aadhaar: form.aadhaar.replace(/\s/g, ""),
        address: {
          full_address: form.full_address.trim(),
          city: form.city.trim(),
          state: form.state.trim(),
          pincode: form.pincode.trim(),
        },
        bank: {
          account_name: form.account_name.trim(),
          bank_name: form.bank_name.trim(),
          account_number: form.account_number.trim(),
          confirm_account_number: form.confirm_account_number.trim(),
          branch_name: form.branch_name.trim(),
          ifsc_code: form.ifsc_code.trim().toUpperCase(),
          account_type: form.account_type || "savings",
        },
        files,
      };
      const res = formMode === "edit"
        ? await HttpService.editChannelPartner(editingRecord._id, payload)
        : await HttpService.onboardChannelPartner(payload);
      if (res?.data?.success) {
        props.setSnackInfo(res.data.message || (formMode === "edit" ? "Channel Partner updated" : "Submitted for Finance approval"), "success");
        closeForm();
        fetchChannelPartners();
      } else {
        props.setSnackInfo(res?.data?.message || "Submission failed", "error");
      }
    } catch (err) {
      const errs = err?.response?.data?.errors;
      props.setSnackInfo(
        (err?.response?.data?.message || "Submission failed") + (Array.isArray(errs) ? `: ${errs.join(", ")}` : ""),
        "error"
      );
    }
    setSubmitting(false);
  };

  const renderTextField = (key, extraProps = {}) => {
    const aadhaarOptional = key === "aadhaar" && formMode === "edit";
    return (
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label={aadhaarOptional ? `${FIELD_LABELS[key]} (optional)` : FIELD_LABELS[key]}
          value={form[key]}
          onChange={(e) => setField(key, e.target.value)}
          error={!!errors[key]}
          helperText={errors[key] || (aadhaarOptional ? "Leave blank to keep the existing Aadhaar number" : " ")}
          variant="outlined"
          size="small"
          required={!aadhaarOptional}
          {...extraProps}
        />
      </Grid>
    );
  };

  const renderUpload = (key) => (
    <Grid item xs={12} sm={4}>
      <Typography variant="body2" style={{ marginBottom: 6 }}>{FIELD_LABELS[key]} *</Typography>
      <label className={classes.uploadBox} style={{ display: "block", borderColor: errors[key] ? "#f44336" : undefined }}>
        <input
          type="file"
          accept=".pdf,.jpg,.jpeg,.png"
          style={{ display: "none" }}
          onChange={(e) => handleFileUpload(key, e.target.files?.[0])}
        />
        {uploading[key] ? (
          <CircularProgress size={22} />
        ) : files[key]?.s3Link ? (
          <>
            <CheckCircleIcon style={{ color: "#4caf50" }} />
            <div className={classes.fileName}>{files[key].name}</div>
          </>
        ) : (
          <>
            <CloudUploadIcon color="action" />
            <Typography variant="caption" display="block">Click to upload</Typography>
          </>
        )}
      </label>
      {errors[key] && <Typography variant="caption" color="error">{errors[key]}</Typography>}
    </Grid>
  );

  return (
    <div className={classes.root}>
      <Paper className={classes.header}>
        <Box>
          <Typography variant="h5" className={classes.headerTitle}>
            <PeopleAltIcon fontSize="large" />
            Channel Partners
          </Typography>
          <Typography variant="body2" className={classes.headerSubtitle}>
            All onboarded Channel Partners, most recently updated first.
          </Typography>
        </Box>
        <Box className={classes.headerActions}>
          <Button
            variant="outlined"
            className={classes.raiseButton}
            startIcon={<PaymentIcon />}
            onClick={openPayoutDialog}
          >
            Raise Payment
          </Button>
          <Button
            className={classes.createButton}
            startIcon={<AddIcon />}
            onClick={openCreateForm}
          >
            Create New
          </Button>
        </Box>
      </Paper>

      <Box className={classes.toolbar}>
        <Box display="flex" alignItems="center" style={{ gap: 8 }}>
          <TextField
            className={classes.searchField}
            placeholder="Search by name…"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") submitSearch(); }}
            variant="outlined"
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
              endAdornment: (searchInput || appliedSearch) && (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={clearSearch}>
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <Button
            variant="contained"
            color="primary"
            size="small"
            className={classes.downloadButton}
            onClick={submitSearch}
            style={{ height: 40 }}
          >
            Search
          </Button>
        </Box>
        <Tooltip title="Download the list currently shown as a CSV file">
          <span>
            <Button
              className={classes.downloadButton}
              variant="outlined"
              color="primary"
              startIcon={<DownloadIcon />}
              onClick={handleDownloadCsv}
              disabled={channelPartners.length === 0}
            >
              Download CSV
            </Button>
          </span>
        </Tooltip>
      </Box>

      <TableContainer component={Paper} className={classes.tablePaper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell className={classes.tableHeadCell}>Sl. No.</TableCell>
              <TableCell className={classes.tableHeadCell}>CP Code</TableCell>
              <TableCell className={classes.tableHeadCell}>Name</TableCell>
              <TableCell className={classes.tableHeadCell}>Mobile</TableCell>
              <TableCell className={classes.tableHeadCell}>PAN Number</TableCell>
              <TableCell className={classes.tableHeadCell}>Bank A/C No.</TableCell>
              <TableCell className={classes.tableHeadCell}>Onboarded By</TableCell>
              <TableCell className={classes.tableHeadCell}>Updated</TableCell>
              <TableCell className={classes.tableHeadCell}>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loadingList && (
              <TableRow><TableCell colSpan={9} align="center" style={{ padding: 32 }}><CircularProgress size={24} /></TableCell></TableRow>
            )}
            {!loadingList && channelPartners.length === 0 && (
              <TableRow>
                <TableCell colSpan={9}>
                  <Box className={classes.emptyState}>
                    <PeopleAltIcon style={{ fontSize: 48, color: "#c5cae9", marginBottom: 8 }} />
                    <Typography variant="body1" color="textSecondary">
                      {appliedSearch ? `No Channel Partners match "${appliedSearch}"` : "No Channel Partners yet"}
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            )}
            {!loadingList && channelPartners.map((record, index) => (
              <TableRow key={record._id} className={classes.tableRow} onClick={() => openDetails(record)}>
                <TableCell className={classes.tableCell}>{page * rowsPerPage + index + 1}</TableCell>
                <TableCell className={classes.tableCell}>{record.cp_code || "-"}</TableCell>
                <TableCell className={classes.tableCell}>
                  <Box className={classes.nameCell}>
                    <Avatar className={classes.avatar}>
                      {(record.full_name || "?").trim().charAt(0).toUpperCase()}
                    </Avatar>
                    <Typography variant="body2" className={classes.fullName}>{record.full_name}</Typography>
                  </Box>
                </TableCell>
                <TableCell className={classes.tableCell}>{record.mobile}</TableCell>
                <TableCell className={classes.tableCell}>{record.pan_number || "-"}</TableCell>
                <TableCell className={classes.tableCell}>{record.bank?.account_number || "-"}</TableCell>
                <TableCell className={classes.tableCell}>{record.onboarded_by?.name || "-"}</TableCell>
                <TableCell className={classes.tableCell}>{record.updated_at ? new Date(record.updated_at).toLocaleString() : "-"}</TableCell>
                <TableCell className={classes.tableCell}>
                  <Chip
                    size="small"
                    label={STATUS_LABELS[record.status] || record.status}
                    className={classes.statusChip}
                    style={STATUS_COLORS[record.status] || {}}
                  />
                  {record.status === "rejected" && record.remarks && (
                    <Typography variant="caption" display="block" color="error">{record.remarks}</Typography>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <div className={classes.paginationContainer}>
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(e, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
          labelRowsPerPage="Rows per page:"
        />
      </div>

      <Dialog open={formMode !== null} onClose={() => !submitting && closeForm()} maxWidth="md" fullWidth>
        <DialogTitle>{formMode === "edit" ? "Edit Channel Partner" : "Onboard a New Channel Partner"}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" style={{ marginBottom: 20 }}>
            {formMode === "edit"
              ? "Every field is mandatory except Aadhaar. If this Channel Partner was already approved or rejected, saving will resubmit it to the Finance department for approval."
              : "Every field is mandatory. This goes to the Finance department for approval."}
          </Typography>

          <Typography variant="subtitle1" className={classes.sectionTitle}>Personal Details</Typography>
          <Grid container spacing={2}>
            {renderTextField("full_name")}
            {renderTextField("email")}
            {renderTextField("mobile", { inputProps: { maxLength: 10 } })}
            {renderTextField("pan_number", { inputProps: { maxLength: 10, style: { textTransform: "uppercase" } } })}
            {renderTextField("aadhaar", { inputProps: { maxLength: 12 } })}
            {renderTextField("full_address")}
            {renderTextField("city")}
            {renderTextField("state")}
            {renderTextField("pincode", { inputProps: { maxLength: 6 } })}
          </Grid>
          <Grid container spacing={2} style={{ marginTop: 8, marginBottom: 24 }}>
            {renderUpload("pan_doc")}
            {renderUpload("aadhaar_doc")}
          </Grid>

          <Typography variant="subtitle1" className={classes.sectionTitle}>Banking Details</Typography>
          <Grid container spacing={2}>
            {renderTextField("account_name")}
            {renderTextField("bank_name")}
            {renderTextField("account_number", { inputProps: { maxLength: 18 } })}
            {renderTextField("confirm_account_number", { inputProps: { maxLength: 18 } })}
            {renderTextField("branch_name")}
            {renderTextField("ifsc_code", { inputProps: { style: { textTransform: "uppercase" } } })}
            <Grid item xs={12} sm={6}>
              <TextField
                select
                fullWidth
                label={FIELD_LABELS.account_type}
                value={form.account_type}
                onChange={(e) => setField("account_type", e.target.value)}
                variant="outlined"
                size="small"
                required
              >
                {ACCOUNT_TYPES.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                ))}
              </TextField>
            </Grid>
          </Grid>
          <Grid container spacing={2} style={{ marginTop: 8 }}>
            {renderUpload("cheque_doc")}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeForm} disabled={submitting}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained" color="primary" disabled={submitting}>
            {submitting
              ? <CircularProgress size={18} color="inherit" />
              : (formMode === "edit" ? "Save & Resubmit" : "Submit for Approval")}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={detailsOpen} onClose={closeDetails} maxWidth="md" fullWidth PaperProps={{ style: { borderRadius: 16 } }}>
        {selectedRecord && (
          <>
            <DialogTitle>
              <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" style={{ gap: 8 }}>
                <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                  <Avatar className={classes.avatar}>
                    {(selectedRecord.full_name || "?").trim().charAt(0).toUpperCase()}
                  </Avatar>
                  <Box>
                    <Typography variant="inherit">{selectedRecord.full_name}</Typography>
                    {selectedRecord.cp_code && (
                      <Typography variant="caption" style={{ color: "#5b6b82", fontWeight: 600 }}>
                        {selectedRecord.cp_code}
                      </Typography>
                    )}
                  </Box>
                </Box>
                <Chip
                  size="small"
                  label={STATUS_LABELS[selectedRecord.status] || selectedRecord.status}
                  className={classes.statusChip}
                  style={STATUS_COLORS[selectedRecord.status] || {}}
                />
              </Box>
            </DialogTitle>
            <DialogContent dividers>
              {CHANNEL_PARTNER_TYPE?.renderDetails
                ? CHANNEL_PARTNER_TYPE.renderDetails(selectedRecord, { setSnackInfo: props.setSnackInfo })
                : null}

              {(() => {
                const docs = CHANNEL_PARTNER_TYPE?.documents ? (CHANNEL_PARTNER_TYPE.documents(selectedRecord) || []) : [];
                if (docs.length === 0) return null;
                return (
                  <Box mt={3}>
                    <Typography variant="subtitle2" style={{ fontWeight: 700, marginBottom: 8 }}>
                      Documents
                    </Typography>
                    <Box display="flex" flexWrap="wrap" style={{ gap: 8 }}>
                      {docs.map((doc) => (
                        <Button
                          key={doc.label}
                          variant="outlined"
                          color="primary"
                          size="small"
                          component="a"
                          href={doc.url}
                          target="_blank"
                          rel="noreferrer"
                          startIcon={<DescriptionIcon />}
                        >
                          {doc.label}
                        </Button>
                      ))}
                    </Box>
                  </Box>
                );
              })()}
            </DialogContent>
            <DialogActions>
              {canEdit(selectedRecord) && (
                <Button
                  onClick={() => openEditForm(selectedRecord)}
                  startIcon={<EditIcon />}
                  color="primary"
                >
                  Edit
                </Button>
              )}
              <Button onClick={closeDetails}>Close</Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      <Dialog open={payoutDialogOpen} onClose={() => !payoutSubmitting && closePayoutDialog()} maxWidth="sm" fullWidth>
        <DialogTitle>Raise Channel Partner Payout</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" style={{ marginBottom: 20 }}>
            5% of the subtotal of the lead's finalized quotation (matching the lead's current version) — sent to Finance for approval.
          </Typography>

          <Typography variant="subtitle1" className={classes.sectionTitle}>1. Select Channel Partner</Typography>
          <FormControl variant="outlined" fullWidth size="small" style={{ marginBottom: 24 }}>
            <InputLabel>Approved Channel Partner</InputLabel>
            <Select
              value={payoutChannelPartnerId}
              onChange={(e) => setPayoutChannelPartnerId(e.target.value)}
              label="Approved Channel Partner"
            >
              {activeChannelPartners.length === 0 && (
                <MenuItem value="" disabled>No approved Channel Partners yet</MenuItem>
              )}
              {activeChannelPartners.map((cp) => (
                <MenuItem key={cp._id} value={cp._id}>
                  {cp.full_name} — {cp.mobile}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Typography variant="subtitle1" className={classes.sectionTitle}>2. Lead Number</Typography>
          <TextField
            fullWidth
            label="Lead No. / Unique Lead Number"
            value={payoutLeadNo}
            onChange={(e) => setPayoutLeadNo(e.target.value)}
            variant="outlined"
            size="small"
            helperText="The lead's source must be Channel Partner and Customer Design Sign Off must be complete. The payout amount (5% of the subtotal of the lead's finalized quotation) is calculated automatically."
          />

          {lastRaisedPayout && (
            <Box style={{ background: "#e8f5e9", borderRadius: 12, padding: 16, textAlign: "center", marginTop: 20 }}>
              <Typography variant="body2" color="textSecondary">
                Last raised for lead {lastRaisedPayout.uniqueLeadNumber || lastRaisedPayout.lead_no || "-"} — {lastRaisedPayout.payout_percentage}% of quotation subtotal ₹{Number(lastRaisedPayout.subTotal || 0).toLocaleString("en-IN")}
              </Typography>
              <Typography variant="h4" style={{ fontWeight: 700, color: "#2e7d32" }}>
                ₹{Number(lastRaisedPayout.amount || 0).toLocaleString("en-IN")}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closePayoutDialog} disabled={payoutSubmitting}>Close</Button>
          <Button
            onClick={handleRaisePayout}
            variant="contained"
            color="primary"
            disabled={!canSubmitPayout}
          >
            {payoutSubmitting ? <CircularProgress size={18} color="inherit" /> : "Raise Payout for Approval"}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default connect(null, { setLoading, setSnackInfo })(ChannelPartnerOnboarding);
